var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getToken } from '@/utils';
import { normalizeArtifactLimitError } from '@/utils/artifact-limit';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { writeAccessToken, readAccessToken, readRefreshToken } from '@/utils/auth-token-store';
// Base API configuration
let API_BASE_URL = '';
let AUTH_TOKEN_KEY = '';
let accessed_from = '';
let VRITI_API_BASE_URL = process.env.REACT_APP_VRITI_BASE_URL;
export const setApiBaseUrl = (apiBaseUrl) => {
    API_BASE_URL = apiBaseUrl;
};
export const setAuthTokenKey = (key) => {
    AUTH_TOKEN_KEY = key;
};
export const setAccessedFrom = (from) => {
    accessed_from = from;
};
function parseErrorBody(response) {
    return __awaiter(this, void 0, void 0, function* () {
        // Try to parse JSON body, fallback to text
        try {
            const text = yield response.text();
            if (!text)
                return null;
            let parsed;
            try {
                parsed = JSON.parse(text);
            }
            catch (_a) {
                return { error: response.statusText, details: text };
            }
            // Every client method rejects with this parsed body, and most callers toast
            // `err.message || err.error` straight through — so a package-limit rejection is
            // rewritten here, once, into recruiter-facing copy instead of the raw artifact
            // location ("selection_counts.both_count limit exceeded"). Deliberately outside
            // the parse `try`: a throw in here must not be mistaken for invalid JSON and
            // collapse a real error body into the bare statusText.
            return normalizeArtifactLimitError(parsed);
        }
        catch (e) {
            return { error: response.statusText, details: String((e === null || e === void 0 ? void 0 : e.message) || e) };
        }
    });
}
const getHeaders = (customHeaders = {}) => {
    const headers = Object.assign({ 'Content-Type': 'application/json' }, customHeaders);
    // Conditionally add Authorization if token exists
    // Fall back to localStorage.getItem('access') in case AUTH_TOKEN_KEY hasn't
    // been configured yet (e.g. on the very first render before useSetApiConfig runs)
    const token = (AUTH_TOKEN_KEY ? getToken(AUTH_TOKEN_KEY) : null) || localStorage.getItem('access');
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};
// Reusable token / base-url accessors so isolated modules (e.g. the Vriti
// staffing-resume flow) can build their own requests without duplicating the
// token-resolution rule or re-reading the env var. Mirrors getHeaders()' token
// fallback so behaviour stays identical to the shared clients.
export const getAuthToken = () => (AUTH_TOKEN_KEY ? getToken(AUTH_TOKEN_KEY) : null) || localStorage.getItem('access');
export const getVritiBaseUrl = () => VRITI_API_BASE_URL || '';
// True only for the standalone ("self") ai-interview app — not embedded as a
// child service (no `accessed_from` from the host and not inside an iframe).
const isSelfApp = () => {
    const inIframe = typeof window !== 'undefined' && window.self !== window.top;
    return !accessed_from && !inIframe;
};
// Append app_name=ai-interview to a request URL, but ONLY for the self app.
// Child-service (embedded) requests are left untouched so the parent's own
// app_name scoping is preserved. Exported so components with their own fetch
// helpers (e.g. dashboard views) can apply the same rule.
export const withAppName = (url) => {
    if (!isSelfApp())
        return url;
    if (/[?&]app_name=/.test(url))
        return url;
    return `${url}${url.includes('?') ? '&' : '?'}app_name=ai-interview`;
};
export const vritiApiClient = {
    get: (url_1, ...args_1) => __awaiter(void 0, [url_1, ...args_1], void 0, function* (url, customHeaders = {}) {
        try {
            const response = yield fetch(withAppName(`${VRITI_API_BASE_URL}${url}`), {
                method: 'GET',
                headers: getHeaders(customHeaders),
            });
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                // If backend returned structured JSON, reject that so callers receive it
                if (parsed && typeof parsed === 'object') {
                    return Promise.reject(parsed);
                }
                return Promise.reject({
                    error: `GET request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            return response.json();
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
    // Public GET — no Authorization header
    getPublic: (url) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield fetch(withAppName(`${VRITI_API_BASE_URL}${url}`), {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                if (parsed && typeof parsed === 'object') {
                    return Promise.reject(parsed);
                }
                return Promise.reject({
                    error: `GET request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            return response.json();
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
    // `options.headers` overrides the defaults from getHeaders() — needed by flows
    // that must authenticate with a one-off token instead of the stored `access`
    // one (e.g. the forgot-password reset step, where the user is signed out and
    // the credential is the short-lived token minted by otp-verify).
    // `options.skipAuth` omits the Authorization header entirely. Public auth
    // endpoints (login, forgot-password, otp-verify) reject a stale/foreign token
    // with 401 "Token is invalid" BEFORE looking at the body, so sending one left
    // over in localStorage turns a normal response into a hard failure.
    post: (url, data, options) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const requestUrl = `${VRITI_API_BASE_URL}${url}`;
            const baseHeaders = (options === null || options === void 0 ? void 0 : options.skipAuth)
                ? { 'Content-Type': 'application/json', vriti: 'ai' }
                : getHeaders({ vriti: 'ai' });
            const response = yield fetch((options === null || options === void 0 ? void 0 : options.skipAppName) ? requestUrl : withAppName(requestUrl), {
                method: 'POST',
                headers: Object.assign(Object.assign({}, baseHeaders), ((options === null || options === void 0 ? void 0 : options.headers) || {})),
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                if (parsed && typeof parsed === 'object') {
                    return Promise.reject(parsed);
                }
                return Promise.reject({
                    error: `POST request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            return response.json();
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
    postFormData: (url, formData) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const token = getToken(AUTH_TOKEN_KEY) || localStorage.getItem('access') || null;
            const headers = {};
            if (token)
                headers['Authorization'] = `Bearer ${token}`;
            // Do NOT set Content-Type — browser sets it with boundary for FormData
            const response = yield fetch(withAppName(`${VRITI_API_BASE_URL}${url}`), {
                method: 'POST',
                headers,
                body: formData,
            });
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                if (parsed && typeof parsed === 'object')
                    return Promise.reject(parsed);
                return Promise.reject({
                    error: `POST request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            return response.json();
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
    delete: (url_1, data_1, ...args_1) => __awaiter(void 0, [url_1, data_1, ...args_1], void 0, function* (url, data, customHeaders = {}) {
        try {
            const response = yield fetch(withAppName(`${VRITI_API_BASE_URL}${url}`), Object.assign({ method: 'DELETE', headers: getHeaders(customHeaders) }, (data !== undefined ? { body: JSON.stringify(data) } : {})));
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                if (parsed && typeof parsed === 'object')
                    return Promise.reject(parsed);
                return Promise.reject({
                    error: `DELETE request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            const text = yield response.text();
            try {
                return text ? JSON.parse(text) : null;
            }
            catch (_a) {
                return text;
            }
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
    patch: (url, data) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const tokenKeyToUse = AUTH_TOKEN_KEY;
            const token = tokenKeyToUse ? getToken(tokenKeyToUse) : null;
            const response = yield fetch(withAppName(`${VRITI_API_BASE_URL}${url}`), {
                method: 'PATCH',
                headers: getHeaders({ vriti: 'ai' }),
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                if (parsed && typeof parsed === 'object') {
                    return Promise.reject(parsed);
                }
                return Promise.reject({
                    error: `PATCH request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            return response.json();
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
};
// API client with common configuration
export const apiClient = {
    get: (url) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield fetch(withAppName(`${API_BASE_URL}${url}`), {
                method: 'GET',
                headers: getHeaders(),
            });
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                // If backend returned structured JSON, reject that so callers receive it
                if (parsed && typeof parsed === 'object') {
                    return Promise.reject(parsed);
                }
                return Promise.reject({
                    error: `GET request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            return response.json();
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
    getBlob: (url, signal) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield fetch(withAppName(`${API_BASE_URL}${url}`), {
                method: 'GET',
                headers: getHeaders(),
                signal,
            });
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                if (parsed && typeof parsed === 'object') {
                    return Promise.reject(parsed);
                }
                return Promise.reject({
                    error: `GET request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            const disposition = response.headers.get('Content-Disposition');
            const filenameMatch = disposition === null || disposition === void 0 ? void 0 : disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            const filename = filenameMatch ? filenameMatch[1].replace(/['"]/g, '') : undefined;
            const buffer = yield response.arrayBuffer();
            return { buffer, filename };
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
    post: (url, data) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield fetch(withAppName(`${API_BASE_URL}${url}`), {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                if (parsed && typeof parsed === 'object') {
                    return Promise.reject(parsed);
                }
                return Promise.reject({
                    error: `POST request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            return response.json();
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
    postFormData: (url, formData) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const token = (AUTH_TOKEN_KEY ? getToken(AUTH_TOKEN_KEY) : null) || localStorage.getItem('access');
            const headers = {};
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
            const response = yield fetch(withAppName(`${API_BASE_URL}${url}`), {
                method: 'POST',
                headers,
                body: formData,
            });
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                if (parsed && typeof parsed === 'object') {
                    return Promise.reject(parsed);
                }
                return Promise.reject({
                    error: `POST request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            return response.json();
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
    patch: (url, data) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield fetch(withAppName(`${API_BASE_URL}${url}`), {
                method: 'PATCH',
                headers: getHeaders(),
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                if (parsed && typeof parsed === 'object') {
                    return Promise.reject(parsed);
                }
                return Promise.reject({
                    error: `PATCH request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            return response.json();
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
    put: (url, data) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield fetch(withAppName(`${API_BASE_URL}${url}`), {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                if (parsed && typeof parsed === 'object') {
                    return Promise.reject(parsed);
                }
                return Promise.reject({
                    error: `PUT request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            return response.json();
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
    delete: (url) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield fetch(withAppName(`${API_BASE_URL}${url}`), {
                method: 'DELETE',
                headers: getHeaders(),
            });
            if (!response.ok) {
                const parsed = yield parseErrorBody(response);
                if (parsed && typeof parsed === 'object') {
                    return Promise.reject(parsed);
                }
                return Promise.reject({
                    error: `DELETE request failed: ${response.statusText}`,
                    details: parsed || '',
                });
            }
            // Some DELETE endpoints may return an empty body
            const text = yield response.text();
            try {
                return text ? JSON.parse(text) : null;
            }
            catch (_a) {
                return text;
            }
        }
        catch (err) {
            return Promise.reject({ error: 'Request failed', details: (err === null || err === void 0 ? void 0 : err.message) || String(err) });
        }
    }),
};
// Query keys for consistent caching
export const queryKeys = {
    representatives: (jd_id, page) => ['representatives', jd_id, page],
    jobDescriptions: (orgId, page, search, page_size) => ['jobDescriptions', orgId, page, search, page_size], // ✅ now callable
    jobDescription: (id, orgId) => ['jobDescription', id, orgId],
    jobDescriptionReference: (id, orgId) => ['jobDescription', id, orgId],
    jdInterviews: (jdId) => ['jdInterviews', jdId],
    interviewSetups: ['interviewSetups'],
    interviewSetup: (id) => ['interviewSetup', id],
    jobCriteria: (jobId) => ['jobCriteria', jobId],
    criterion: (jobId, criterionId) => ['criterion', jobId, criterionId],
    interviewReport: (interviewSlug) => ['interview-slug', interviewSlug],
    locales: ['locales'],
    azureVoices: (locale, gender) => ['azureVoices', locale, gender],
    openaiVoices: ['openaiVoices'],
    jdPreview: (id) => ['jdPreview', id],
    voiceProviders: ['voiceProviders'],
    voiceProviderOptions: (providerId, language) => ['voiceProviderOptions', providerId, language],
};
export const useLocales = () => {
    return useQuery({
        queryKey: queryKeys.locales,
        queryFn: () => apiClient.get('/azure/voices/locale'),
    });
};
export const useAzureVoices = (locale, gender) => {
    return useQuery({
        queryKey: queryKeys.azureVoices(locale, gender),
        queryFn: () => apiClient.get(`/azure/voices?locale=${locale || ''}&gender=${gender || ''}`),
        enabled: !!locale && !!gender,
        refetchOnWindowFocus: false, // ✅ ADD
        refetchOnReconnect: false,
    });
};
// Enabled providers for the schedule-interview picker (secret-free).
export const useVoiceProviders = (enabled = true) => {
    return useQuery({
        queryKey: queryKeys.voiceProviders,
        queryFn: () => apiClient.get('/voice/providers/'),
        enabled,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};
// Language + voice options for a chosen provider (voices filtered by language).
export const useVoiceProviderOptions = (providerId, language) => {
    return useQuery({
        queryKey: queryKeys.voiceProviderOptions(providerId, language),
        queryFn: () => apiClient.get(`/voice/providers/${providerId}/options/?language=${language || ''}`),
        enabled: !!providerId,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};
export const adminVoiceProviderKeys = {
    list: ['adminVoiceProviders'],
};
export const useAdminVoiceProviders = (enabled = true) => {
    return useQuery({
        queryKey: adminVoiceProviderKeys.list,
        queryFn: () => apiClient.get('/admin/voice/providers/'),
        enabled,
        refetchOnWindowFocus: false,
    });
};
export const useCreateVoiceProvider = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => apiClient.post('/admin/voice/providers/', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminVoiceProviderKeys.list });
            queryClient.invalidateQueries({ queryKey: queryKeys.voiceProviders });
        },
    });
};
export const useUpdateVoiceProvider = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ providerId, data }) => apiClient.put(`/admin/voice/providers/${providerId}/`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminVoiceProviderKeys.list });
            queryClient.invalidateQueries({ queryKey: queryKeys.voiceProviders });
        },
    });
};
export const useDeleteVoiceProvider = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (providerId) => apiClient.delete(`/admin/voice/providers/${providerId}/`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminVoiceProviderKeys.list });
            queryClient.invalidateQueries({ queryKey: queryKeys.voiceProviders });
        },
    });
};
// ── Provider connectivity tests + verification gate (onboarding) ──────────────────────
export const useTestVoiceProviderTTS = () => useMutation({
    mutationFn: ({ providerId, phrase, language, voice, }) => apiClient.post(`/admin/voice/providers/${providerId}/test/tts/`, {
        phrase,
        language,
        voice,
    }),
});
export const useTestVoiceProviderSTT = () => useMutation({
    mutationFn: ({ providerId, audioBase64, language, voice, }) => apiClient.post(`/admin/voice/providers/${providerId}/test/stt/`, {
        audio_base64: audioBase64,
        language,
        voice,
    }),
});
export const useVadTest = () => useMutation({
    mutationFn: ({ pcmBase64, threshold, onsetMinFrames, silenceTimeoutMs, pregateRms, }) => apiClient.post('/admin/voice/test/vad/', {
        pcm_base64: pcmBase64,
        threshold,
        onset_min_frames: onsetMinFrames,
        silence_timeout_ms: silenceTimeoutMs,
        pregate_rms: pregateRms,
    }),
});
export const useVerifyVoiceProviderLeg = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ providerId, leg, ok, error, }) => apiClient.post(`/admin/voice/providers/${providerId}/verify/`, {
            leg,
            ok,
            error,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminVoiceProviderKeys.list });
            queryClient.invalidateQueries({ queryKey: queryKeys.voiceProviders });
        },
    });
};
/** Run the backend MediaPipe FaceMesh on one composed frame (admin test bench). */
export const useProctoringDetect = () => useMutation({
    mutationFn: ({ frameBase64 }) => apiClient.post('/admin/proctoring/test/detect/', {
        frame_base64: frameBase64,
    }),
});
export const proctoringConfigKeys = {
    admin: ['proctoringConfigAdmin'],
};
export const useProctoringConfigAdmin = (enabled = true) => useQuery({
    queryKey: proctoringConfigKeys.admin,
    queryFn: () => apiClient.get('/admin/proctoring/config/'),
    enabled,
    refetchOnWindowFocus: false,
});
export const useUpdateProctoringConfig = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ captureWidth }) => apiClient.put('/admin/proctoring/config/', {
            capture_width: captureWidth,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: proctoringConfigKeys.admin });
        },
    });
};
export const useOpenAIVoices = () => {
    return useQuery({
        queryKey: queryKeys.openaiVoices,
        queryFn: () => apiClient.get('/openai/voices/'),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};
export const fetchOpenAIVoiceSample = (voice_1, ...args_1) => __awaiter(void 0, [voice_1, ...args_1], void 0, function* (voice, language = 'en-US', signal) {
    const { buffer } = yield apiClient.getBlob(`/openai/voices/sample/?voice=${voice}&language=${language}`, signal);
    const blob = new Blob([buffer], { type: 'audio/mpeg' });
    return URL.createObjectURL(blob);
});
/**
 * VA-27731: a short spoken sample of one cost-optimized provider voice.
 *
 * Deliberately the same shape as `fetchOpenAIVoiceSample` above — an AUTHENTICATED
 * fetch to a blob URL, not a URL handed to `new Audio()`. The endpoint is IAM-gated
 * and an <audio> element cannot send an Authorization header, so pointing one at the
 * API answers 403 and the sample silently fails to play. Only the legacy Azure
 * picker can get away with a bare URL, because that one plays a public file Azure
 * itself hosts.
 *
 * The backend returns WAV (it wraps the vendor's raw PCM, which no browser plays
 * directly) and caches per provider+voice+language, so a second listen is free.
 */
export const fetchVoiceProviderSample = (providerId, voice, language, signal) => __awaiter(void 0, void 0, void 0, function* () {
    const params = new URLSearchParams({ voice });
    if (language)
        params.set('language', language);
    const { buffer } = yield apiClient.getBlob(`/voice/providers/${encodeURIComponent(providerId)}/sample/?${params}`, signal);
    const blob = new Blob([buffer], { type: 'audio/wav' });
    return URL.createObjectURL(blob);
});
export const useInterviewReport = (interviewSlug) => {
    return useQuery({
        queryKey: queryKeys.interviewReport(interviewSlug),
        queryFn: () => apiClient.get(`/get-interview/?slug=${interviewSlug}`),
        enabled: !!interviewSlug,
        // A finalized interview report is immutable, so there is nothing to
        // re-fetch when the browser tab regains focus. Without these the default
        // QueryClient refetches on every window focus, which flipped `isFetching`
        // and made the whole report flash back to the loading skeleton (and reset
        // to the first tab) each time the user switched browser tabs and returned.
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: 5 * 60 * 1000,
    });
};
export const useJobDescriptions = (orgId, page = 1, search = '', page_size = 6, enabled = true) => {
    const token = (AUTH_TOKEN_KEY ? getToken(AUTH_TOKEN_KEY) : null) || localStorage.getItem('access');
    return useQuery({
        queryKey: queryKeys.jobDescriptions(orgId, page, search, page_size),
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            let url = `/jds?page=${page}`;
            if (orgId)
                url += `&org_id=${orgId}`;
            if (search)
                url += `&${search}`;
            if (page_size)
                url += `&page_size=${page_size}`;
            return apiClient.get(url);
        }),
        enabled: enabled && !!token,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
    });
};
export const useJobDescription = (id, orgId) => {
    return useQuery({
        queryKey: queryKeys.jobDescription(id, orgId),
        queryFn: () => apiClient.get(`/jd/${id}${orgId ? `?org_id=${orgId}` : ''}`),
        enabled: !!id, // Only fetch if id exists
        refetchOnWindowFocus: false, // ✅ ADD
        refetchOnReconnect: false,
    });
};
export const useJobDescriptionByJobReference = (id, orgId) => {
    return useQuery({
        queryKey: queryKeys.jobDescriptionReference(id),
        queryFn: () => apiClient.get(`/jd/job-reference/${id}${orgId ? `?org_id=${orgId}` : ''}`),
        enabled: !!id, // Only fetch if id exists
        refetchOnWindowFocus: false, // ✅ ADD
        refetchOnReconnect: false,
    });
};
export const useJDInterviews = (jdId, enabled = true) => {
    return useQuery({
        queryKey: queryKeys.jdInterviews(jdId),
        queryFn: () => apiClient.get(`/jd/${jdId}/interviews/`),
        enabled: enabled && !!jdId,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};
export const useJDPreview = (id, enabled = false) => {
    return useQuery({
        queryKey: queryKeys.jdPreview(id),
        queryFn: () => apiClient.get(`/jd/${id}/preview/`),
        enabled: !!id && enabled,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};
export const useInterviewsByJd = (jobId) => {
    return useQuery({
        queryKey: ['interviewsByJob', jobId],
        queryFn: () => apiClient.get(`/interviews?jd_id=${jobId}`),
        enabled: !!jobId,
    });
};
// Criteria GET Hooks
export const useJobCriteria = (jobId) => {
    return useQuery({
        queryKey: queryKeys.jobCriteria(jobId),
        queryFn: () => apiClient.get(`/jobs/${jobId}/criteria/`),
        enabled: !!jobId,
        staleTime: 5 * 60 * 1000,
    });
};
export const useCriterion = (jobId, criterionId) => {
    return useQuery({
        queryKey: queryKeys.criterion(jobId, criterionId),
        queryFn: () => apiClient.get(`/jobs/${jobId}/criteria/${criterionId}`),
        enabled: !!jobId && !!criterionId,
    });
};
// POST Hooks
export const useCreateJobDescription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => apiClient.post('/jds/', data),
        onSuccess: () => {
            // Invalidate and refetch job descriptions list
            queryClient.invalidateQueries({ queryKey: queryKeys.jobDescriptions() });
        },
        onError: (error) => {
            console.error('Failed to create job description:', error);
        },
    });
};
export const useCreateInterviewSetup = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => apiClient.post('/interview-setups', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.interviewSetups });
        },
        onError: (error) => {
            console.error('Failed to create interview setup:', error);
        },
    });
};
// Criteria POST Hook
export const useCreateCriterion = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ jobId, data, }) => apiClient.post(`/jobs/${jobId}/criteria/`, data),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.jobCriteria(variables.jobId) });
        },
        onError: (error) => {
            console.error('Failed to create criterion:', error);
        },
    });
};
export const useRescheduleInterview = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data }) => apiClient.post(`/reschedule-interview/`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['jobDescriptions'],
                exact: false, // refresh all pages
            });
            // Reschedulable-interviews list changed too — refresh so a reopened modal is fresh.
            queryClient.invalidateQueries({ queryKey: ['jdInterviews'], exact: false });
        },
        onError: (error) => {
            console.error('Failed to reschedule interview:', error);
        },
    });
};
// PATCH/PUT Hooks
export const useUpdateJobDescription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => apiClient.patch(`/jd/${id}/update`, data),
        onSuccess: (data, variables) => {
            // Update the specific job description in cache
            queryClient.setQueryData(queryKeys.jobDescription(variables.id), data);
            // Invalidate the list to ensure consistency
            queryClient.invalidateQueries({ queryKey: queryKeys.jobDescriptions() });
        },
        onError: (error) => {
            console.error('Failed to update job description:', error);
        },
    });
};
export const useToggleJDStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ jd_id, type }) => apiClient.patch(`/jd-disable/`, {
            jd_id,
            type,
        }),
        onMutate: (_a) => __awaiter(void 0, [_a], void 0, function* ({ jd_id, type }) {
            yield queryClient.cancelQueries({ queryKey: ['jobDescriptions'] });
            const snapshots = [];
            const allQueries = queryClient.getQueriesData({ queryKey: ['jobDescriptions'] });
            for (const [queryKey, oldData] of allQueries) {
                if (!oldData)
                    continue;
                snapshots.push({ queryKey, data: oldData });
                const updater = (item) => item.id === jd_id
                    ? Object.assign(Object.assign({}, item), { is_disabled: type === 'disable', interview_stats: item.interview_stats
                            ? Object.assign(Object.assign({}, item.interview_stats), { scheduled_interviews: type === 'disable' ? 0 : item.interview_stats.disabled || 0, disabled: type === 'disable'
                                    ? (item.interview_stats.disabled || 0) +
                                        (item.interview_stats.scheduled_interviews || 0)
                                    : 0 }) : item.interview_stats }) : item;
                if (Array.isArray(oldData)) {
                    queryClient.setQueryData(queryKey, oldData.map(updater));
                }
                else if (oldData === null || oldData === void 0 ? void 0 : oldData.results) {
                    queryClient.setQueryData(queryKey, Object.assign(Object.assign({}, oldData), { results: oldData.results.map(updater) }));
                }
            }
            return { snapshots };
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.jobDescriptions() });
        },
        onError: (error, _vars, context) => {
            var _a;
            console.error('Failed to toggle JD status:', error);
            (_a = context === null || context === void 0 ? void 0 : context.snapshots) === null || _a === void 0 ? void 0 : _a.forEach(({ queryKey, data }) => {
                queryClient.setQueryData(queryKey, data);
            });
        },
    });
};
export const useCloneJD = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ jd_id }) => apiClient.post(`/job/${jd_id}/clone/`, {}),
        onSuccess: () => {
            // refresh JD list if needed
            queryClient.invalidateQueries({
                queryKey: queryKeys.jobDescriptions(),
            });
        },
        onError: (error) => {
            console.error('Failed to clone JD:', error);
        },
    });
};
export const useCancelInterviews = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data }) => apiClient.patch(`/cancel-interview/`, data),
        onSuccess: (data, variables) => {
            // Update the specific job description in cache
            // queryClient.setQueryData(queryKeys.jobDescription(), data);
            // Invalidate the list to ensure consistency
            queryClient.invalidateQueries({ queryKey: queryKeys.jobDescriptions() });
            // Cancelling removes interviews from the reschedulable set — refresh it too.
            queryClient.invalidateQueries({ queryKey: ['jdInterviews'], exact: false });
        },
        onError: (error) => {
            console.error('Failed to update job description:', error);
        },
    });
};
export const useUpdateInterviewSetup = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => apiClient.patch(`/interview-setups/${id}/`, data),
        onSuccess: (data, variables) => {
            queryClient.setQueryData(queryKeys.interviewSetup(variables.id), data);
            queryClient.invalidateQueries({ queryKey: queryKeys.interviewSetups });
        },
        onError: (error) => {
            console.error('Failed to update interview setup:', error);
        },
    });
};
// Criteria UPDATE Hook
export const useUpdateCriterion = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ jobId, criterionId, data, }) => apiClient.put(`/jobs/${jobId}/criteria/${criterionId}`, data),
        onSuccess: (data, variables) => {
            queryClient.setQueryData(queryKeys.criterion(variables.jobId, variables.criterionId), data);
            queryClient.invalidateQueries({ queryKey: queryKeys.jobCriteria(variables.jobId) });
        },
        onError: (error) => {
            console.error('Failed to update criterion:', error);
        },
    });
};
// DELETE Hooks
export const useDeleteJobDescription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ jobId }) => apiClient.delete(`/jds/${jobId}`),
        onSuccess: (_, { jobId }) => {
            // Remove the detail cache
            queryClient.removeQueries({
                queryKey: queryKeys.jobDescription(jobId),
            });
            // Update the list immediately in UI
            queryClient.setQueryData(queryKeys.jobDescriptions(), (oldData) => {
                if (!oldData)
                    return oldData;
                return Object.assign(Object.assign({}, oldData), { results: oldData.results.filter((job) => job.id !== jobId), count: oldData.count - 1 });
            });
            // Optional: keep this to refetch after cache update
            queryClient.invalidateQueries({
                queryKey: queryKeys.jobDescriptions(),
            });
        },
        onError: (error) => {
            console.error('error:', error);
            // toast.error(error.error);
        },
    });
};
export const useDeleteInterviewSetup = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiClient.delete(`/interview-setups/${id}`),
        onSuccess: (data, id) => {
            queryClient.removeQueries({ queryKey: queryKeys.interviewSetup(id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.interviewSetups });
        },
        onError: (error) => {
            console.error('Failed to delete interview setup:', error);
        },
    });
};
// Criteria DELETE Hook
export const useDeleteCriterion = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ jobId, criterionId }) => apiClient.delete(`/jobs/${jobId}/criteria/${criterionId}`),
        onSuccess: (data, variables) => {
            queryClient.removeQueries({
                queryKey: queryKeys.criterion(variables.jobId, variables.criterionId),
            });
            queryClient.invalidateQueries({ queryKey: queryKeys.jobCriteria(variables.jobId) });
        },
        onError: (error) => {
            console.error('Failed to delete criterion:', error);
        },
    });
};
export const useDeleteJD = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ jobId }) => apiClient.delete(`/jd/${jobId}/`),
        onSuccess: (_, { jobId, orgId, page, search }) => {
            // Remove detail cache
            queryClient.removeQueries({
                queryKey: queryKeys.jobDescription(jobId),
            });
            // Optimistically update list cache
            queryClient.setQueryData(queryKeys.jobDescriptions(orgId, page, search), (old) => {
                if (!(old === null || old === void 0 ? void 0 : old.results))
                    return old;
                return Object.assign(Object.assign({}, old), { results: old.results.filter((item) => item.id !== jobId), count: old.count - 1 });
            });
            // Refetch list just to be safe
            queryClient.invalidateQueries({
                queryKey: queryKeys.jobDescriptions(orgId, page, search),
            });
        },
        onError: (error) => {
            console.error('Failed to delete JD', error);
        },
    });
};
// File upload utility for job descriptions (matching your API)
export const useUploadJobDescription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ file, description, org_id, job_reference_id, parent_id, }) {
            const formData = new FormData();
            if (file) {
                formData.append('file', file);
            }
            // Add optional description if provided
            if (description) {
                formData.append('description', description);
            }
            if (org_id) {
                formData.append('org_id', org_id);
            }
            if (job_reference_id) {
                formData.append('job_reference_id', job_reference_id);
            }
            if (parent_id) {
                formData.append('parent_id', parent_id);
            }
            const tokenKeyToUse = AUTH_TOKEN_KEY;
            const token = tokenKeyToUse ? getToken(tokenKeyToUse) : null;
            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            const response = yield fetch(withAppName(`${API_BASE_URL}/jds/`), {
                method: 'POST',
                body: formData,
                // Don't set Content-Type header, let browser set it with boundary
                // Add authorization if needed
                headers,
            });
            if (!response.ok) {
                const errorText = yield response.text();
                throw new Error(`Upload failed: ${response.statusText} - ${errorText}`);
            }
            return response.json();
        }),
        onSuccess: () => {
            // ✅ Correctly invalidate all jobDescriptions queries
            queryClient.invalidateQueries({ queryKey: queryKeys.jobDescriptions() });
        },
        onError: (error) => {
            console.error('Failed to upload job description:', error);
        },
    });
};
export const useGenerateJD = () => {
    return useMutation({
        mutationFn: (data) => apiClient.post('/jds/generate/', data),
    });
};
export const useRepresentativesList = (jdId, page = 1) => {
    return useQuery({
        queryKey: ['representatives-list', jdId, page],
        queryFn: () => apiClient.get(`/representatives-list/?page=${page}&jd_id=${jdId}`),
        enabled: !!jdId,
    });
};
export const useRepresentativeProfile = (id) => {
    const token = getToken(AUTH_TOKEN_KEY) || localStorage.getItem('access');
    return useQuery({
        queryKey: ['representative-profile', id],
        queryFn: () => vritiApiClient.get(`representative-profile/${id}/`),
        enabled: !!id && !!token,
    });
};
export const useUpdateRepresentativeProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => vritiApiClient.patch(`representative-profile/${id}/`, data),
        onSuccess: (_res, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['representative-profile', id] });
        },
    });
};
export const useUploadRepresentativeImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, formData }) => vritiApiClient.postFormData(`upload-representative-image/`, formData),
        onSuccess: (_res, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['representative-profile', id] });
        },
    });
};
export const useUploadRepresentativeBanner = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, formData }) => vritiApiClient.postFormData(`upload-representative-banner/`, formData),
        onSuccess: (_res, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['representative-profile', id] });
        },
    });
};
export const useRemoveRepresentativeBanner = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id }) => vritiApiClient.delete(`delete-representative-banner/`),
        onSuccess: (_res, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['representative-profile', id] });
        },
    });
};
export const useOrganizationProfile = () => {
    const token = getToken(AUTH_TOKEN_KEY) || localStorage.getItem('access');
    return useQuery({
        queryKey: ['organization-profile'],
        queryFn: () => vritiApiClient.get('get-organization-profile/'),
        enabled: !!token,
    });
};
export const useUpdateOrganizationProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => vritiApiClient.patch(`organization-profile/${id}/`, data),
        onSuccess: () => __awaiter(void 0, void 0, void 0, function* () {
            const fresh = yield vritiApiClient.get('get-organization-profile/');
            queryClient.setQueryData(['organization-profile'], fresh);
        }),
    });
};
export const useUploadOrganizationImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ formData }) => vritiApiClient.postFormData(`upload-organization-profile-image/`, formData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organization-profile'] });
        },
    });
};
export const useUploadOrganizationBanner = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ formData }) => vritiApiClient.postFormData(`upload-organization-profile-banner/`, formData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organization-profile'] });
        },
    });
};
export const useRemoveOrganizationBanner = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => vritiApiClient.delete(`delete-organization-profile-banner/`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organization-profile'] });
        },
    });
};
export const useOrganizationRecruiters = (orgId, page = 1, pageSize = 12, enabled = true) => {
    const token = (AUTH_TOKEN_KEY ? getToken(AUTH_TOKEN_KEY) : null) || localStorage.getItem('access');
    return useQuery({
        queryKey: ['organization-recruiters', orgId, page, pageSize],
        queryFn: () => {
            // Vriti reads the page size from `limit` (defaults to 12); keep page_size
            // too for compatibility. Without `limit`, a custom pageSize is ignored.
            let url = `organization-representative/?page=${page}&limit=${pageSize}&page_size=${pageSize}`;
            if (orgId)
                url += `&organization_id=${orgId}`;
            return vritiApiClient.get(url);
        },
        enabled: !!orgId && !!token && enabled,
    });
};
export const useOrgTree = (orgId, enabled = true) => {
    const token = (AUTH_TOKEN_KEY ? getToken(AUTH_TOKEN_KEY) : null) || localStorage.getItem('access');
    return useQuery({
        queryKey: ['get-org-hierarchical-relation', orgId],
        queryFn: () => vritiApiClient.get(`get-org-hierarchical-relation/?organization_id=${orgId}`),
        enabled: !!orgId && !!token && enabled,
    });
};
export const useRepresentativeJobs = (orgId, page = 1, representativeId, page_size = 6) => {
    const token = (AUTH_TOKEN_KEY ? getToken(AUTH_TOKEN_KEY) : null) || localStorage.getItem('access');
    return useQuery({
        queryKey: ['representativeJobs', orgId, page, representativeId, page_size],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            let url = `/jds?page=${page}`;
            if (orgId)
                url += `&org_id=${orgId}`;
            if (representativeId)
                url += `&representative_id=${representativeId}`;
            if (page_size)
                url += `&page_size=${page_size}`;
            return apiClient.get(url);
        }),
        enabled: !!representativeId && !!token,
    });
};
/**
 * Mint a fresh access token from the stored refresh token, scoped to this app
 * (app_name = "ai-interview"), and overwrite the existing `access` value in
 * localStorage (same key — only the value changes).
 *
 * Non-destructive: returns null and leaves storage untouched when there is no
 * refresh token or the request fails, so it can safely run on every app load.
 */
export const refreshAccessToken = () => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    if (typeof window === 'undefined')
        return null;
    // VA-27781: through the store, so it finds the cookie (and still falls back to
    // a legacy localStorage copy for sessions that predate the change).
    const refresh = readRefreshToken();
    if (!refresh || !VRITI_API_BASE_URL)
        return null;
    // The refresh endpoint requires an Authorization Bearer header (it responds
    // 401 with Www-Authenticate: Bearer otherwise). Send the current access token.
    const currentAccess = readAccessToken() || '';
    try {
        const response = yield fetch(`${VRITI_API_BASE_URL}auth/refresh-token/`, {
            method: 'POST',
            headers: Object.assign({ 'Content-Type': 'application/json' }, (currentAccess ? { Authorization: `Bearer ${currentAccess}` } : {})),
            // Refresh token + app scope go in the body.
            body: JSON.stringify({ refresh_token: refresh, app_name: 'ai-interview' }),
        });
        if (!response.ok)
            return null;
        const data = yield response.json();
        const newAccess = ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.access_token) || (data === null || data === void 0 ? void 0 : data.access_token);
        if (!newAccess)
            return null;
        writeAccessToken(newAccess);
        window.dispatchEvent(new Event('authChange'));
        return newAccess;
    }
    catch (_b) {
        return null;
    }
});
// Download the bulk-upload Excel template.
export const downloadRecruiterTemplate = () => __awaiter(void 0, void 0, void 0, function* () {
    const token = getAuthToken();
    const headers = {};
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    const response = yield fetch(`${API_BASE_URL}/recruiters/invite-template/`, { headers });
    if (!response.ok)
        throw new Error('Failed to download template');
    const blob = yield response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'recruiter_invite_template.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
});
// Import recruiters from a client-provided API — dry-run validate (no invites).
export const useValidateRecruiterSource = () => useMutation({
    mutationFn: (payload) => apiClient.post('/recruiters/source/validate/', payload),
});
export const toVritiRepresentative = (r) => ({
    official_representative_name: r.name,
    official_representative_email: r.email,
    official_mobile_number: r.mobile || '',
    designation: r.designation,
    representative_role: r.role,
    is_super_admin: r.role === 'super-admin',
    reporting_repre_id: r.reporting_to ? Number(r.reporting_to) : 0,
});
export const useCreateOrgRepresentatives = () => useMutation({
    mutationFn: (reps) => 
    // ?source=ai_interview → Vriti syncs the recruiter's email -> IAM account
    // mapping to AI Interview (bulk/API endpoints sync unconditionally).
    vritiApiClient.post('create-org-representatives/?source=ai_interview', reps),
});
// Bulk-invite recruiters — upload the Excel straight to Vriti (Vriti parses +
// creates in the background). AI-INT only provides the template download.
export const useBulkCreateOrgRepresentatives = () => useMutation({
    mutationFn: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return vritiApiClient.postFormData('bulk-create-org-representatives/', formData);
    },
});
// Invite recruiters fetched from a client API — Vriti fetches + creates in the
// background. AI-INT's "test connection" is only a preview before this.
export const useApiCreateOrgRepresentatives = () => useMutation({
    mutationFn: (payload) => vritiApiClient.post('api-create-org-representatives/', payload),
});
// ── Recruiter (subordinate) management: resend / deactivate / reactivate /
// delete + assignee search. All hit Vriti's *-subordinate-own endpoints, which
// require the super-admin/proxy-admin caller and enforce reassignment rules
// server-side (we surface their messages). See organization-profile.tsx.
const invalidateRecruiters = (queryClient) => {
    queryClient.invalidateQueries({ queryKey: ['organization-recruiters'] });
    queryClient.invalidateQueries({ queryKey: ['get-org-hierarchical-relation'] });
};
export const useResendSubordinateInvite = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (representative_id) => vritiApiClient.post('resend-email-subordinate/', { representative_id }),
        onSuccess: () => invalidateRecruiters(queryClient),
    });
};
export const useDeactivateSubordinate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => vritiApiClient.patch('deactivate-subordinate-own/', payload),
        onSuccess: () => invalidateRecruiters(queryClient),
    });
};
export const useReactivateSubordinate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (representative_id) => vritiApiClient.patch('reactivate-subordinate-own/', { representative_id, recover_access: true }),
        onSuccess: () => invalidateRecruiters(queryClient),
    });
};
export const useDeleteSubordinate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => vritiApiClient.delete('delete-subordinate-own/', payload, { vriti: 'ai' }),
        onSuccess: () => invalidateRecruiters(queryClient),
    });
};
// Assignee search for the reassign flow. Enabled by the caller (modal open).
export const useAssigneeDropdown = (search, enabled) => {
    const token = (AUTH_TOKEN_KEY ? getToken(AUTH_TOKEN_KEY) : null) || localStorage.getItem('access');
    return useQuery({
        queryKey: ['assignee-dropdown', search],
        queryFn: () => vritiApiClient.get(`assignee-dropdown-list/?search=${encodeURIComponent(search)}`, { vriti: 'ai' }),
        enabled: enabled && !!token,
        placeholderData: keepPreviousData,
    });
};
export const candidateSlotKeys = {
    context: (token) => ['candidate-slots', token],
};
export const useCandidateSlotContext = (token) => useQuery({
    queryKey: candidateSlotKeys.context(token || ''),
    queryFn: () => apiClient.get(`/candidate-slots/?token=${encodeURIComponent(token || '')}`),
    enabled: Boolean(token),
    // Candidate-facing state that changes on submit; a stale read would show a
    // selection that is no longer there.
    staleTime: 0,
    retry: false,
});
export const useSubmitCandidateSlots = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ token, slots, timezone, }) => apiClient.post('/candidate-slots/submit/', { token, slots, timezone }),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: candidateSlotKeys.context(variables.token),
            });
        },
    });
};
